import React, { useState, useEffect, useMemo, useRef } from "react";
import DataTable from "react-data-table-component";
import DataTableExtensions from "react-data-table-component-extensions";
import "react-data-table-component-extensions/dist/index.css";
import { connect } from "react-redux";
import {
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Input,
} from "reactstrap";
import CashSessionSummary from "./CashSessionSummary";
import AIChatButton from "../../components/Common/AIChatButton";

import moment from "moment";
import { formatCurrency } from "../../utils/formatCurrency";
moment.locale("es");

const TableCurrentCash = (props) => {
  const [selectedButton, setSelectedButton] = useState(0);
  const [currentCash, setCurrentCash] = useState([]);
  const [allCash, setAllCash] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [totals, setTotals] = useState([]);

  // Al cambiar de sesión, volver al inicio del panel derecho: si el operador
  // estaba scrolleado leyendo movimientos, el resumen de la sesión nueva le
  // quedaba fuera de pantalla y parecía que no había pasado nada.
  const rightPanelRef = useRef(null);

  const handleButtonClick = (index) => {
    setSelectedButton(index);
    setCurrentCash(allCash[index]);

    if (rightPanelRef.current && rightPanelRef.current.scrollIntoView) {
      rightPanelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const items = [...props.cash].reverse();
    setAllCash(items);
    setCurrentCash(items[0]);
  }, [props.cash]);

  // OJO: las tres listas vienen del store en orden cronológico y acá se
  // invierten para mostrar la sesión más reciente arriba. Las TRES tienen que
  // invertirse igual: comparten el índice `selectedButton`. Si una queda sin
  // invertir, la pantalla mezcla datos de sesiones distintas.
  useEffect(() => {
    const items = [...props.resumes].reverse();
    setResumes(items);
  }, [props.resumes]);

  useEffect(() => {
    const items = [...(props.sessionTotals || [])].reverse();
    setTotals(items);
  }, [props.sessionTotals]);

  const columns = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      width: "80px",
      cellExport: (row) => row.id,
    },
    {
      name: "FECHA",
      selector: (row) => row.date,
      sortable: true,
      width: "110px",
      cellExport: (row) => row.date,
    },
    {
      name: "HORA",
      selector: (row) => row.time,
      sortable: true,
      width: "80px",
      cellExport: (row) => row.time,
    },
    {
      name: "DESCRIPCIÓN",
      selector: (row) => row.desc,
      sortable: true,
      cell: (row) => <span style={{ fontWeight: 600 }}>{row.desc}</span>,
      cellExport: (row) => row.desc,
    },
    {
      name: "OPERADOR",
      selector: (row) => row.operator,
      sortable: true,
      width: "160px",
      cellExport: (row) => row.operator,
    },
    {
      name: "F. PAGO",
      selector: (row) => row.payment,
      sortable: true,
      width: "120px",
      cell: (row) => <PaymentBadge payment={row.payment} />,
      cellExport: (row) => row.payment,
    },
    {
      name: "IMPORTE",
      selector: (row) => row.rawValue,
      sortable: true,
      width: "120px",
      right: true,
      // Los negativos (anulaciones, retiros, devoluciones) en rojo, igual que
      // en la pantalla de Caja del sistema. Sin esto se leen como un cobro más.
      cell: (row) => (
        <span
          style={{
            fontWeight: 700,
            color: row.rawValue < 0 ? "var(--bs-danger)" : undefined,
          }}
        >
          {row.value}
        </span>
      ),
      cellExport: (row) => row.value,
    },
  ];

  // Fila completa con fondo rojo tenue cuando el movimiento es negativo.
  const conditionalRowStyles = [
    {
      when: (row) => row.rawValue < 0,
      style: {
        backgroundColor: "rgba(var(--bs-danger-rgb), 0.06)",
      },
    },
  ];

  // Build AI data summary from ALL sessions
  const aiDataSummary = useMemo(() => {
    if (!allCash.length) return "";

    const allMovements = allCash.flat();
    const grandTotal = allMovements.reduce((a, m) => a + (m.value || 0), 0);

    let summary = `RESUMEN GENERAL DE CAJA\n`;
    summary += `Total de sesiones: ${allCash.length}\n`;
    summary += `Total de movimientos: ${allMovements.length}\n`;
    summary += `Recaudacion total: ${formatCurrency(grandTotal, true)}\n\n`;

    // Global payment method breakdown
    const globalByPayment = {};
    allMovements.forEach((m) => {
      const key = m.fpay || "Sin forma de pago";
      if (!globalByPayment[key]) globalByPayment[key] = { count: 0, total: 0 };
      globalByPayment[key].count++;
      globalByPayment[key].total += m.value || 0;
    });
    summary += `TOTAL POR FORMA DE PAGO:\n`;
    Object.entries(globalByPayment).forEach(([k, v]) => {
      const pct = grandTotal > 0 ? ((v.total / grandTotal) * 100).toFixed(0) : 0;
      summary += `- ${k}: ${v.count} mov., ${formatCurrency(v.total, true)} (${pct}%)\n`;
    });
    summary += "\n";

    // Global type breakdown
    const allTickets = allMovements.filter((m) => m.description?.startsWith("Ticket"));
    const allCC = allMovements.filter((m) => m.description?.startsWith("Pago CC"));
    const allFondo = allMovements.filter((m) => m.description?.includes("Fondo"));
    const allOthers = allMovements.filter((m) => !m.description?.startsWith("Ticket") && !m.description?.startsWith("Pago CC") && !m.description?.includes("Fondo"));

    summary += `TOTAL POR TIPO:\n`;
    if (allTickets.length) {
      const t = allTickets.reduce((a, m) => a + (m.value || 0), 0);
      const avg = t / allTickets.length;
      summary += `- Tickets cobrados: ${allTickets.length}, total ${formatCurrency(t, true)}, promedio ${formatCurrency(avg, true)}\n`;
    }
    if (allCC.length) {
      const t = allCC.reduce((a, m) => a + (m.value || 0), 0);
      summary += `- Pagos mensuales (CC): ${allCC.length}, total ${formatCurrency(t, true)}\n`;
    }
    if (allFondo.length) {
      const t = allFondo.reduce((a, m) => a + (m.value || 0), 0);
      summary += `- Fondo de caja: ${allFondo.length}, total ${formatCurrency(t, true)}\n`;
    }
    if (allOthers.length) {
      const t = allOthers.reduce((a, m) => a + (m.value || 0), 0);
      summary += `- Otros: ${allOthers.length}, total ${formatCurrency(t, true)}\n`;
    }
    summary += "\n";

    // Per-session detail
    summary += `DETALLE POR SESION:\n`;
    allCash.forEach((session, i) => {
      const d = session[0];
      const r = resumes[i];
      const total = Array.isArray(r) ? r.find((x) => x.name === "Total") : null;
      const op = d?.user_fullName || "Desconocido";
      const date = d?.date ? moment(d.date).format("DD/MM/YYYY HH:mm") : "";
      const ticketCount = session.filter((m) => m.description?.startsWith("Ticket")).length;
      const ccCount = session.filter((m) => m.description?.startsWith("Pago CC")).length;

      summary += `\n[Sesion ${i + 1}] ${op} - ${date}\n`;
      summary += `  Total: ${total ? formatCurrency(total.value, true) : "?"}\n`;
      summary += `  Movimientos: ${session.length} (${ticketCount} tickets, ${ccCount} mensuales)\n`;

      // Payment methods for this session
      if (Array.isArray(r)) {
        r.filter((x) => x.name !== "Total").forEach((x) => {
          summary += `  ${x.name}: ${formatCurrency(x.value, true)}${x.percent ? ` (${Math.round(x.percent)}%)` : ""}\n`;
        });
      }
    });

    // List of individual movements (top 5 highest value across all sessions)
    const sorted = [...allMovements].sort((a, b) => (b.value || 0) - (a.value || 0));
    const top5 = sorted.slice(0, 5);
    if (top5.length) {
      summary += `\nMOVIMIENTOS MAS ALTOS:\n`;
      top5.forEach((m) => {
        summary += `- ${formatCurrency(m.value, true)} | ${m.description} | ${m.fpay || "-"} | ${m.user_fullName} | ${moment(m.date).format("DD/MM HH:mm")}\n`;
      });
    }

    return summary;
  }, [allCash, resumes]);

  const aiSuggestions = useMemo(() => [
    "¿Cuanto se recaudo en total?",
    "¿Como se distribuyen las formas de pago?",
    "¿Que operador recaudo mas?",
    "¿Cuantos tickets vs mensuales se cobraron?",
    "¿Cuales fueron los movimientos mas altos?",
    "¿Que porcentaje se cobro en efectivo?",
  ], []);

  if (allCash.length === 0)
    return <div className="p-4 text-center text-muted">No hay datos de caja</div>;

  // El fondo de caja queda FUERA de la tabla: es plata que ya estaba, no un
  // movimiento de la operación. Se informa arriba, en el resumen de la sesión.
  const items = (currentCash || [])
    .filter((item) => item.type !== "init")
    .map((item) => ({
      id: item.id,
      date: moment(item.date).format("DD/MM/YYYY"),
      time: moment(item.date).format("HH:mm"),
      desc: item.description,
      operator: item.user_fullName,
      payment: item.fpay,
      value: formatCurrency(item.value, true),
      rawValue: item.value,
      rawDate: item.date,
    }));

  const tableData = { columns, data: items };

  return (
    <>
      <Row className="mt-3">
        {/* Selector de sesiones */}
        <Col xl={2} lg={3} md={12} className="mb-3">
          <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: 12, letterSpacing: 0.5 }}>
            <i className="fas fa-users me-1"></i> Sesiones
          </h6>

          {/* Desktop: lista vertical con scroll propio.
              Sin esto, buscar una sesión vieja obliga a scrollear toda la
              página y el resumen y la tabla de la derecha se van de pantalla. */}
          <div
            className="d-none d-lg-block"
            style={{
              maxHeight: "calc(100vh - 190px)",
              overflowY: "auto",
              overflowX: "hidden",
              paddingRight: 6,
              position: "sticky",
              top: 80,
            }}
          >
            {allCash.map((item, index) => {
              const d = item[0];
              const isActive = selectedButton === index;
              return (
                <Card
                  key={index}
                  className={`mb-2 shadow-sm cursor-pointer`}
                  style={{
                    background: isActive ? "var(--bs-primary)" : "#fff",
                    borderLeft: isActive ? "3px solid var(--bs-primary)" : "3px solid transparent",
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                  onClick={() => handleButtonClick(index)}
                >
                  <CardBody className="py-2 px-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div style={{ fontSize: 13, fontWeight: isActive ? 700 : 500, color: isActive ? "#fff" : "inherit" }}>
                          {d.user_fullName}
                        </div>
                        <div style={{ fontSize: 11, color: isActive ? "rgba(255,255,255,0.75)" : "#6c757d" }}>
                          <i className="fas fa-calendar-alt me-1"></i>
                          {moment(d.date).format("DD/MM/YYYY HH:mm")}
                        </div>
                      </div>
                      {resumes[index] && Array.isArray(resumes[index]) && (() => {
                        const total = resumes[index].find(r => r.name === "Total");
                        return total ? (
                          <span style={{ fontSize: 13, fontWeight: 700, color: isActive ? "#fff" : "#495057", whiteSpace: "nowrap" }}>
                            {formatCurrency(total.value)}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  </CardBody>
                </Card>
              );
            })}
          </div>

          {/* Mobile/Tablet: select dropdown */}
          <div className="d-lg-none">
            <Input
              type="select"
              bsSize="sm"
              value={selectedButton}
              onChange={(e) => handleButtonClick(Number(e.target.value))}
            >
              {allCash.map((item, index) => {
                const d = item[0];
                return (
                  <option key={index} value={index}>
                    {d.user_fullName} — {moment(d.date).format("DD/MM/YYYY HH:mm")}
                  </option>
                );
              })}
            </Input>
          </div>
        </Col>

        {/* Resumen de la sesión + tabla de movimientos */}
        <Col xl={10} lg={9} md={12}>
          {/* Ancla de scroll. Va un div propio porque el Col de reactstrap no
              acepta ref (sus props son solo tag/xs/sm/md/lg/xl/xxl/className/
              cssModule/widths): pasarle innerRef fallaba en silencio.

              scrollMarginTop = alto del header fijo (70px, $header-height) +
              aire. Sin esto scrollIntoView deja el ancla pegada al borde
              superior y el header tapa el resumen de la sesión. */}
          <div ref={rightPanelRef} style={{ scrollMarginTop: 86 }} />

          <CashSessionSummary
            totals={totals[selectedButton]}
            byPayment={resumes[selectedButton]}
          />

          <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: 12, letterSpacing: 0.5 }}>
            <i className="fas fa-list me-1"></i> Movimientos ({items.length})
            <span className="text-muted text-lowercase ms-2" style={{ fontSize: 11, letterSpacing: 0 }}>
              — sin el fondo de caja, que se informa arriba
            </span>
          </h6>

          {/* Desktop: DataTable */}
          <div className="d-none d-md-block">
            <Card className="shadow-sm">
              <CardBody className="p-0">
                <DataTableExtensions
                  {...tableData}
                  exportHeaders={false}
                  filterPlaceholder="Buscar..."
                >
                  <DataTable
                    pagination
                    highlightOnHover
                    dense
                    paginationPerPage={30}
                    paginationRowsPerPageOptions={[30, 100, 200]}
                    paginationComponentOptions={{
                      rowsPerPageText: "Filas:",
                      rangeSeparatorText: "de",
                      noRowsPerPage: false,
                    }}
                    noDataComponent={
                      <div className="p-4 text-center text-muted">Sin movimientos</div>
                    }
                    persistTableHead
                    customStyles={customTableStyles}
                    conditionalRowStyles={conditionalRowStyles}
                  />
                </DataTableExtensions>
              </CardBody>
            </Card>
          </div>

          {/* Mobile: Cards */}
          <div className="d-md-none">
            {items.length === 0 ? (
              <div className="text-center text-muted py-4">Sin movimientos</div>
            ) : (
              items.map((item, i) => (
                <Card
                  key={i}
                  className="mb-2 shadow-sm"
                  style={{
                    borderLeft: `3px solid var(--bs-${item.rawValue < 0 ? "danger" : "primary"})`,
                    backgroundColor:
                      item.rawValue < 0 ? "rgba(var(--bs-danger-rgb), 0.06)" : undefined,
                  }}
                >
                  <CardBody className="py-2 px-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <span style={{ fontSize: 14, fontWeight: 700 }}>
                        {item.desc}
                      </span>
                      <span
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: item.rawValue < 0 ? "var(--bs-danger)" : undefined,
                        }}
                      >
                        {item.value}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        <i className="fas fa-calendar-alt me-1"></i>{item.date}
                        <span className="ms-2">
                          <i className="fas fa-clock me-1"></i>{item.time}
                        </span>
                      </div>
                      <PaymentBadge payment={item.payment} />
                    </div>
                    {item.operator && (
                      <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                        <i className="fas fa-user me-1"></i>{item.operator}
                      </div>
                    )}
                  </CardBody>
                </Card>
              ))
            )}
          </div>
        </Col>
      </Row>

      {/* El aviso de auditoría vive ahora en components/Common/NotaDatoOriginal,
          compartido con Estadísticas y Resumen Diario, y lo pone Pages/Cash al
          pie. El que estaba acá decía lo mismo pero peor: "pueden presentar
          diferencias menores" siembra la duda sin decir qué hacer con ella,
          "base sincronizada" es jerga, y trataba de usted mientras el resto de
          la aplicación trata de tú. */}

      {/* AI Chat */}
      {aiDataSummary && (
        <AIChatButton
          context="caja"
          dataSummary={aiDataSummary}
          suggestions={aiSuggestions}
          title="Asistente de Caja"
        />
      )}
    </>
  );
};

/** Badge de forma de pago */
const PaymentBadge = ({ payment }) => {
  if (!payment) return <span className="text-muted" style={{ fontSize: 11 }}>—</span>;

  const lower = payment.toLowerCase();
  let color = "secondary";
  let icon = "fas fa-receipt";

  if (lower.includes("efectivo")) {
    color = "success";
    icon = "fas fa-money-bill-wave";
  } else if (lower.includes("mercado") || lower.includes("mp")) {
    color = "info";
    icon = "fab fa-cc-stripe";
  } else if (lower.includes("tarjeta") || lower.includes("card")) {
    color = "warning";
    icon = "fas fa-credit-card";
  }

  return (
    <Badge color={color} className={color === "warning" ? "text-dark" : ""} style={{ fontSize: 10 }}>
      <i className={`${icon} me-1`}></i>{payment}
    </Badge>
  );
};

/** Estilos personalizados para DataTable */
const customTableStyles = {
  headCells: {
    style: {
      fontWeight: 700,
      fontSize: 12,
      textTransform: "uppercase",
      color: "#495057",
    },
  },
  cells: {
    style: {
      fontSize: 13,
    },
  },
  rows: {
    style: {
      minHeight: "38px",
    },
  },
};

const mapStateToProps = (state) => ({
  cash: state.cash.cash_items,
  resumes: state.cash.resume_cash_items,
  sessionTotals: state.cash.session_totals,
});

export default connect(mapStateToProps)(TableCurrentCash);
